import {
    CURRENCY_USD,
    GOOGLE_BUSINESS_VERTICAL,
    ITEM_CATEGORY_TICKET,
    ITEM_LIST_NAMES,
    MATCH_TYPE_DIRECT,
    MATCH_TYPE_MIXED,
    MATCH_TYPE_NEARBY,
    MODE_MIXED,
} from './constants';

import type {
    EcommerceItem,
    I18nMoneyFactory,
    PricingEntry,
    TripItemInput,
    TripLeg,
} from './types';

// GA4 drops keys it cannot read anyway; stripping them here keeps the pushed
// payload the same shape as what the container receives, which is what the Tag
// Assistant preview is verified against. Shallow by design -- `items` is an
// array the caller has already built.
function compact<T extends object>(value: T): T {
    return Object.entries(value).reduce((acc, [key, entry]) => (entry === undefined
        ? acc
        : { ...acc, [key]: entry }), {} as T);
}

function roundMoney(value?: number): number | undefined {
    return typeof value === 'number' && Number.isFinite(value)
        ? parseFloat(value.toFixed(2))
        : undefined;
}

/**
 * One mode per item. A trip whose legs disagree (a bus leg into a train leg) is
 * `mixed`, matching the suffix the route key carries.
 */
function resolveMode(modes?: string[]): string | undefined {
    const distinct = [...new Set((modes || [])
        .filter(Boolean)
        .map((mode) => String(mode).toLowerCase()))];

    if (!distinct.length) {
        return undefined;
    }

    return distinct.length === 1
        ? distinct[0]
        : MODE_MIXED;
}

/**
 * `{depart_city_code}_{arrive_city_code}_{mode}` — the remarketing feed's key.
 * Direction and mode are both significant: NYC_DCA_bus, DCA_NYC_bus and
 * NYC_DCA_train are three different items.
 */
function buildRouteKey({
    arriveCityCode,
    departCityCode,
    mode,
}: {
    arriveCityCode?: string;
    departCityCode?: string;
    mode?: string;
}): string | undefined {
    if (!arriveCityCode || !departCityCode || !mode) {
        return undefined;
    }

    return `${departCityCode.toUpperCase()}_${arriveCityCode.toUpperCase()}_${mode.toLowerCase()}`;
}

/**
 * The display name GA4 pairs with a list id. Derived, never passed in, so an
 * item cannot carry a name that belongs to a different list.
 */
function resolveItemListName(itemListId?: string): string | undefined {
    return itemListId
        ? ITEM_LIST_NAMES[itemListId]
        : undefined;
}

function resolveMatchType(isNearby?: boolean): string | undefined {
    if (isNearby === undefined) {
        return undefined;
    }

    return isNearby
        ? MATCH_TYPE_NEARBY
        : MATCH_TYPE_DIRECT;
}

/**
 * A ticket item. `item_category` is the fixed literal `ticket` — the sGTM
 * margin template branches on it and returns no net revenue for anything else,
 * so the mode lives in `item_category2` and the route key's suffix only.
 *
 * When the city codes are unknown the route key is omitted rather than
 * substituted: a trip id in `item_id` would look like a route key to everything
 * downstream of the warehouse.
 */
function buildTripItem({
    arriveCityCode,
    carrierCode,
    departCityCode,
    endDate,
    isNearby,
    itemListId,
    legRole,
    modes,
    price,
    quantity,
    startDate,
    tripId,
}: TripItemInput): EcommerceItem {
    const mode = resolveMode(modes);
    const itemId = buildRouteKey({ arriveCityCode, departCityCode, mode });

    return compact<EcommerceItem>({
        end_date: endDate,
        google_business_vertical: GOOGLE_BUSINESS_VERTICAL,
        item_brand: carrierCode,
        item_category: ITEM_CATEGORY_TICKET,
        item_category2: mode,
        item_id: itemId,
        item_list_id: itemListId,
        item_list_name: resolveItemListName(itemListId),
        item_variant: tripId,
        leg_role: legRole,
        match_type: resolveMatchType(isNearby),
        origin: itemId && departCityCode
            ? departCityCode.toUpperCase()
            : undefined,
        price: roundMoney(price),
        quantity,
        start_date: startDate,
    });
}

/**
 * A cart item for a whole trip, given the legs the store holds it as.
 *
 * The route key is the trip's own endpoints -- a BOS->PHL->DCA itinerary is
 * `BOS_DCA_*`, not two keys, because the two intermediate hops are not a
 * sellable route in the remarketing feed. `item_brand` is the first leg's
 * carrier: on a multi-carrier itinerary that is the one the traveler was shown
 * on the search card, and GA4 has no room for a second.
 */
function buildTripItemFromLegs({
    legs = [],
    ...rest
}: Omit<TripItemInput, 'arriveCityCode' | 'carrierCode' | 'departCityCode' | 'modes'> & {
    legs?: TripLeg[];
}): EcommerceItem {
    const lastLeg = legs[legs.length - 1];

    return buildTripItem({
        ...rest,
        arriveCityCode: lastLeg?.arriveCityCode,
        carrierCode: legs[0]?.carrierCode,
        departCityCode: legs[0]?.departCityCode,
        modes: legs.reduce<string[]>(
            (modes, leg) => [...modes, ...(leg.tripModes || [])],
            [],
        ),
    });
}

const NO_ROUTE_KEY = '__no_route_key__';

/**
 * `view_item_list` grain: one item per distinct route key, `quantity` the
 * number of trips behind it and `price` the cheapest of them. Per-trip detail
 * (trip id, carrier) survives only where every collapsed trip agrees on it —
 * a route served by two carriers has no single `item_brand` to report.
 */
function collapseTripItems(items: EcommerceItem[]): EcommerceItem[] {
    const groups = new Map<string, EcommerceItem[]>();

    items.forEach((item) => {
        const key = item.item_id || NO_ROUTE_KEY;
        groups.set(key, [...(groups.get(key) || []), item]);
    });

    return [...groups.values()].map((group) => {
        const keys = [...new Set(group.flatMap((item) => Object.keys(item)))];

        const merged = keys.reduce((acc, key) => {
            const values = group.map((item) => item[key as keyof EcommerceItem]);
            const [first] = values;

            return {
                ...acc,
                [key]: values.every((value) => value === first)
                    ? first
                    : undefined,
            };
        }, {} as EcommerceItem);

        const prices = group
            .map((item) => item.price)
            .filter((price): price is number => price !== undefined);

        return compact<EcommerceItem>({
            ...merged,
            price: prices.length
                ? Math.min(...prices)
                : undefined,
            quantity: group.reduce((total, item) => total + (item.quantity ?? 1), 0),
        });
    });
}

/**
 * The event-level rollup Google Ads segments on. Unlike the item-level field it
 * has a third value for a list holding both kinds of match.
 */
function rollUpMatchType(items: EcommerceItem[]): string | undefined {
    const matchTypes = new Set(items
        .map((item) => item.match_type)
        .filter(Boolean));

    if (!matchTypes.size) {
        return undefined;
    }

    return matchTypes.size === 1
        ? [...matchTypes][0]
        : MATCH_TYPE_MIXED;
}

/**
 * The basket the event's own items add up to, or nothing.
 *
 * An item with no price makes the total a guess that reads as a real number
 * downstream: the event would claim a basket smaller than the items it lists
 * beside it. All or nothing is the honest answer, and it matches
 * `convertPricingToUsd`, which reports no price for a trip when any of its legs
 * could not be priced.
 */
function sumItemsValue(items: EcommerceItem[]): number | undefined {
    if (!items.length || items.some((item) => typeof item.price !== 'number')) {
        return undefined;
    }

    return roundMoney(items.reduce(
        (total, item) => total + (item.price as number) * (item.quantity ?? 1),
        0,
    ));
}

/**
 * One leg's fare in USD.
 *
 * A carrier that prices in USD already has the exact number, so it is read
 * rather than converted -- the same `pricing.USD` the Canopy bridge treats as
 * "this trip is priced in USD". Anything else is converted once, straight from
 * the currency the carrier quoted to USD.
 *
 * The earlier version went through `getTotalPrice`, which converts into the
 * traveler's *display* currency, and then converted that to USD: a CAD-viewing
 * traveler looking at a USD fare got it rounded out and back through two rates.
 * That was justified by matching `useTotalCartPriceUsd`, but GA4's `value` is
 * fares-only and deliberately does not reconcile with the fee-inclusive
 * Amplitude cart total, so there is no agreement left to preserve -- only
 * accuracy to gain.
 */
function legToUsd(entry: PricingEntry, i18nMoney: I18nMoneyFactory): number | undefined {
    const nativeUsd = entry?.[CURRENCY_USD];

    if (typeof nativeUsd === 'number' && Number.isFinite(nativeUsd)) {
        return nativeUsd;
    }

    // Any remaining key will do: a pricing map holds one fare expressed in
    // several currencies, not several fares, so each entry converts to the same
    // USD amount. `calculatePricingInUSD` and `getTotalPrice` read the first
    // entry for the same reason. USD is preferred above only to skip the
    // conversion, not because the others disagree.
    const [currency, price] = Object.entries(entry || {})[0] || [];

    if (typeof price !== 'number' || !Number.isFinite(price)) {
        return undefined;
    }

    const usd = i18nMoney(price, currency).setCurrency(CURRENCY_USD).getConvertedPrice();

    return Number.isFinite(usd)
        ? usd
        : undefined;
}

/**
 * Sums a trip's per-leg pricing into USD, optionally down to a unit fare.
 * `i18nMoney` is injected rather than imported so this module stays free of the
 * currency service (and so the arithmetic is testable on its own).
 */
function convertPricingToUsd({
    i18nMoney,
    pricing,
    seats = 1,
}: {
    i18nMoney: I18nMoneyFactory;
    pricing?: PricingEntry[];
    seats?: number;
}): number | undefined {
    if (!pricing?.length) {
        return undefined;
    }

    const legs = pricing.map((entry) => legToUsd(entry, i18nMoney));

    // A leg nobody could price makes the trip's total a guess, so report none.
    if (legs.some((leg) => leg === undefined)) {
        return undefined;
    }

    return roundMoney(
        (legs as number[]).reduce((total, leg) => total + leg, 0) / (seats || 1),
    );
}

export {
    buildRouteKey,
    buildTripItem,
    buildTripItemFromLegs,
    collapseTripItems,
    compact,
    convertPricingToUsd,
    resolveItemListName,
    resolveMatchType,
    resolveMode,
    rollUpMatchType,
    sumItemsValue,
};
